'use client';

import { Suspense as RouteSuspense } from 'react';
import { PageLoading as RouteLoading } from '@/app/Components/LoadingBubbles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { useAuth } from '@/app/Components/AuthProvider';
import { Button } from '@/app/Components/Button';
import { PageLoading } from '@/app/Components/LoadingBubbles';
import Modal from '@/app/Components/Modal';
import ProfessionalInfoSection from '@/app/Components/ProfessionalInfoSection';
import { useUiPreferences } from '@/app/Components/UiPreferencesProvider';
import { useOnboardingStatus } from '@/app/hooks/useOnboardingStatus';
import { createCustomAcademicPalettePreset } from '@/app/lib/academicPalette';
import { supabase } from '@/app/lib/supabase';
import { getSafeRedirectPath } from '@/app/services/authHelpers';
import { loadCatalogCourses } from '@/app/services/courseCatalogClient';
import type { CourseGradeEntry } from '@/app/services/userAcademicData';
import type { Degree, RutgersCourse, UserProfile } from '@/app/types/interfaces';
import { courseListsApi, degreePlansApi, degreesApi, friendshipsApi, getUserProfileData, schedulesApi, stripeApi, usersApi } from '../services/api';

import {
  ProfileWorkspaceView,
  type InteractionFeedbackMode,
  type ProfileAcademicData,
  type ProfileCourseList,
  type ProfileDegreePlan,
  type ProfileMemory,
  type ProfileSchedule,
  type BillingPanelProps,
} from './ProfileWorkspaceView';
import {
  createProfileTabUrl,
  createProfileViewModel,
  filterUniqueProfileCourses,
  isProfileTabId,
  summarizeProfileFriendships,
  type ProfileConnectionSummary,
  profileTabIds,
  validateProfileAcademicData,
  type ProfileTabId,
} from './profileViewModel';

const defaultCourseSwatches = [
  { label: 'Crimson', solid: '#c81e3a' }, { label: 'Amber', solid: '#d97706' },
  { label: 'Teal', solid: '#0f766e' }, { label: 'Blue', solid: '#2563eb' },
];
const defaultProgramSwatches = [
  { label: 'Rose', solid: '#db2777' }, { label: 'Gold', solid: '#ca8a04' },
  { label: 'Emerald', solid: '#059669' }, { label: 'Indigo', solid: '#4f46e5' },
];

function ProfilePage() {
  return <ProfilePageWorkspace />;
}

export function ProfilePageWorkspace({ embedded = false, forcedTab }: { embedded?: boolean; forcedTab?: ProfileTabId }) {
  const { user, loading, signOut } = useAuth();
  const {
    academicPalettePreferences, academicPalettePresets, setCoursePaletteId, setProgramPaletteId,
    addCustomAcademicPalette, deleteCustomAcademicPalette, resetAcademicPalettePreferences,
    explorerPreferences, setExplorerDefaultView, setExplorerDepartmentColorsEnabled,
  } = useUiPreferences();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { markOnboardingComplete, isLoading: onboardingLoading } = useOnboardingStatus();

  const [isLoading, setIsLoading] = useState(true);
  const [profileLoadError, setProfileLoadError] = useState<string | null>(null);
  const [profileRetrievedAt, setProfileRetrievedAt] = useState<Date | null>(null);
  const [userObject, setUserObject] = useState<UserProfile | null>(null);
  const [savedSchedules, setSavedSchedules] = useState<ProfileSchedule[]>([]);
  const [courseLists, setCourseLists] = useState<ProfileCourseList[]>([]);
  const [degreePlans, setDegreePlans] = useState<ProfileDegreePlan[]>([]);
  const [degreePlansRetrieved, setDegreePlansRetrieved] = useState(false);
  const [primaryScheduleId, setPrimaryScheduleId] = useState<string | null>(null);
  const [connectionsSummary, setConnectionsSummary] = useState<ProfileConnectionSummary | null>(null);
  const [activeTab, setActiveTab] = useState<ProfileTabId>(() => {
    if (forcedTab) return forcedTab;
    const requested = searchParams.get('tab');
    return isProfileTabId(requested) ? requested : 'profile';
  });
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const pendingTabFocusRef = useRef<ProfileTabId | null>(null);
  const loadedProfileUserIdRef = useRef<string | null>(null);
  const profileLoadRevisionRef = useRef(0);
  const academicLoadRevisionRef = useRef(0);
  const academicSavePendingRef = useRef(false);
  const academicSaveRevisionRef = useRef(0);

  const [academicData, setAcademicData] = useState<ProfileAcademicData>({ year: '', majors: [], school: '', referral_source: '' });
  const [academicErrors, setAcademicErrors] = useState<Record<string, string>>({});
  const [isEditingAcademic, setIsEditingAcademic] = useState(false);
  const [allDegrees, setAllDegrees] = useState<Degree[]>([]);
  const [majorSearch, setMajorSearch] = useState('');

  const [completedCourses, setCompletedCourses] = useState<RutgersCourse[]>([]);
  const [courseGrades, setCourseGrades] = useState<CourseGradeEntry[]>([]);
  const [allCourses, setAllCourses] = useState<RutgersCourse[]>([]);
  const [courseSearchQuery, setCourseSearchQuery] = useState('');
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [isSavingCourses, setIsSavingCourses] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [academicSaveError, setAcademicSaveError] = useState<string | null>(null);
  const completedCourseBaselineRef = useRef<string[] | null>(null);
  const gradeBaselineRef = useRef<CourseGradeEntry[] | null>(null);
  const academicOwnerRef = useRef<string | null>(null);

  const [memories, setMemories] = useState<ProfileMemory[]>([]);
  const [isLoadingMemories, setIsLoadingMemories] = useState(false);
  const [billing, setBilling] = useState<Pick<BillingPanelProps, 'state' | 'accessStatus' | 'accessUntil' | 'subscriptionStatus' | 'cancelAtPeriodEnd' | 'currentPeriodEnd' | 'hasBillingAccount' | 'error'>>({
    state: 'idle', accessStatus: 'free', accessUntil: null, subscriptionStatus: null, cancelAtPeriodEnd: false, currentPeriodEnd: null, hasBillingAccount: false, error: null,
  });
  const [billingActionBusy, setBillingActionBusy] = useState<'checkout' | 'portal' | null>(null);
  const [interactionFeedbackMode, setInteractionFeedbackMode] = useState<InteractionFeedbackMode>('enhanced');
  const [showCustomPaletteBuilder, setShowCustomPaletteBuilder] = useState(false);
  const [customPaletteName, setCustomPaletteName] = useState('');
  const [customPaletteDescription, setCustomPaletteDescription] = useState('');
  const [customPaletteError, setCustomPaletteError] = useState<string | null>(null);
  const [customCourseSwatches, setCustomCourseSwatches] = useState(defaultCourseSwatches);
  const [customProgramSwatches, setCustomProgramSwatches] = useState(defaultProgramSwatches);

  const [messageOpen, setMessageOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('Confirm delete');
  const [confirmMessage, setConfirmMessage] = useState('Are you sure?');
  const [confirmBusy, setConfirmBusy] = useState(false);
  const confirmActionRef = useRef<null | (() => Promise<void>)>(null);

  const routeRedirectTarget = useMemo(() => {
    const query = searchParams.toString();
    return getSafeRedirectPath(`${pathname}${query ? `?${query}` : ''}`);
  }, [pathname, searchParams]);

  useEffect(() => { document.title = 'My Profile - Scarlet Sync'; }, []);
  useEffect(() => {
    const stored = window.localStorage.getItem('scarlet-interaction-feedback');
    if (stored === 'off' || stored === 'important' || stored === 'enhanced') setInteractionFeedbackMode(stored);
  }, []);
  useEffect(() => { void degreesApi.getAll().then(setAllDegrees).catch((error) => console.error('Failed to fetch degrees:', error)); }, []);

  const setInteractionFeedbackPreference = useCallback((mode: InteractionFeedbackMode) => {
    setInteractionFeedbackMode(mode);
    window.localStorage.setItem('scarlet-interaction-feedback', mode);
    window.dispatchEvent(new CustomEvent('scarlet-interaction-feedback-change', { detail: mode }));
  }, []);

  const handleTabChange = useCallback((tab: ProfileTabId) => {
    setActiveTab(tab);
    window.history.replaceState({}, '', createProfileTabUrl(window.location.pathname, window.location.search, tab));
  }, []);

  const loadMemories = useCallback(async () => {
    if (!user?.id) return;
    setIsLoadingMemories(true);
    try {
      const { data, error } = await supabase.from('ai_user_memories').select('id, memory_key, memory_value, created_at, updated_at').eq('user_id', user.id).order('updated_at', { ascending: false });
      if (error) throw error;
      setMemories((data || []) as ProfileMemory[]);
    } catch (error) {
      console.error('Error loading memories:', error);
      setMemories([]);
    } finally { setIsLoadingMemories(false); }
  }, [user?.id]);

  const loadBilling = useCallback(async () => {
    if (!user?.id) return;
    setBilling((current) => ({ ...current, state: 'loading', error: null }));
    try {
      const { data } = await supabase.auth.getSession();
      if (!data.session?.access_token) throw new Error('Sign in again to view billing.');
      const status = await stripeApi.getStatus(data.session.access_token);
      setBilling({
        state: 'ready', accessStatus: status.accessStatus, accessUntil: status.accessUntil,
        subscriptionStatus: status.subscriptionStatus, cancelAtPeriodEnd: status.cancelAtPeriodEnd,
        currentPeriodEnd: status.currentPeriodEnd,
        hasBillingAccount: status.hasBillingAccount, error: null,
      });
    } catch (error) {
      setBilling((current) => ({ ...current, state: 'error', error: error instanceof Error ? error.message : 'Billing could not be loaded.' }));
    }
  }, [user?.id]);

  const openBillingCheckout = useCallback(async () => {
    setBillingActionBusy('checkout');
    try {
      const { data } = await supabase.auth.getSession();
      if (!data.session?.access_token) throw new Error('Sign in again to start checkout.');
      window.location.assign(await stripeApi.createCheckoutSession(data.session.access_token));
    } catch (error) {
      setBillingActionBusy(null);
      setBilling((current) => ({ ...current, state: 'error', error: error instanceof Error ? error.message : 'Checkout could not be started.' }));
    }
  }, []);

  const openBillingPortal = useCallback(async () => {
    setBillingActionBusy('portal');
    try {
      const { data } = await supabase.auth.getSession();
      if (!data.session?.access_token) throw new Error('Sign in again to manage billing.');
      window.location.assign(await stripeApi.getPortalUrl(data.session.access_token));
    } catch (error) {
      setBillingActionBusy(null);
      setBilling((current) => ({ ...current, state: 'error', error: error instanceof Error ? error.message : 'Billing management could not be opened.' }));
    }
  }, []);

  const loadUserData = useCallback(async () => {
    if (!user?.id) return;
    const ownerId = user.id;
    const revision = ++profileLoadRevisionRef.current;
    const academicRevision = ++academicLoadRevisionRef.current;
    setIsLoading(true);
    try {
      const profileData = await getUserProfileData(ownerId);
      if (revision !== profileLoadRevisionRef.current || loadedProfileUserIdRef.current !== ownerId) return;
      setSavedSchedules((profileData.schedules || []) as ProfileSchedule[]);
      setCourseLists((profileData.course_lists || []) as ProfileCourseList[]);
      setUserObject(profileData.user || null);
      setPrimaryScheduleId(profileData.user?.primary_schedule_id ? String(profileData.user.primary_schedule_id) : null);
      const [plansResult, friendshipsResult, onboardingResult, academicResult] = await Promise.allSettled([
        degreePlansApi.getAllUserPlans(ownerId), friendshipsApi.getAllUserFriendships(ownerId),
        (async () => { const result = await supabase.functions.invoke('onboarding', { body: { action: 'get' } }); if (result.error) throw result.error; return result.data; })(),
        (async () => { const { loadUserAcademicData } = await import('@/app/services/userAcademicData'); const result = await loadUserAcademicData(ownerId, { fresh: true }); if (!result) throw new Error('Academic history was unavailable.'); return result; })(),
      ]);
      if (revision !== profileLoadRevisionRef.current || loadedProfileUserIdRef.current !== ownerId) return;
      const failures: string[] = [];
      if (plansResult.status === 'fulfilled') { setDegreePlans((plansResult.value || []) as ProfileDegreePlan[]); setDegreePlansRetrieved(true); } else failures.push('degree plans');
      if (friendshipsResult.status === 'fulfilled') setConnectionsSummary(summarizeProfileFriendships(friendshipsResult.value || [])); else failures.push('friendships');
      if (onboardingResult.status === 'fulfilled' && onboardingResult.value) setAcademicData({ year: onboardingResult.value.year || '', majors: onboardingResult.value.majors || [], school: onboardingResult.value.school || '', referral_source: onboardingResult.value.referral_source || '' }); else if (onboardingResult.status === 'rejected') failures.push('onboarding/profile information');
      if (academicRevision === academicLoadRevisionRef.current && !academicSavePendingRef.current && academicResult.status === 'fulfilled') {
        setCompletedCourses(academicResult.value.completedCourses);
        setCourseGrades(academicResult.value.courseGrades || []);
        setLastSaved(academicResult.value.lastUpdated);
        completedCourseBaselineRef.current = academicResult.value.completedCourses.map((course) => course.baseId);
        gradeBaselineRef.current = academicResult.value.courseGrades || [];
        academicOwnerRef.current = ownerId;
        setAcademicSaveError(null);
      } else if (academicRevision === academicLoadRevisionRef.current && !academicSavePendingRef.current) failures.push('academic history');
      if (failures.length) setProfileLoadError(`Some account data could not be refreshed (${failures.join(', ')}). Last known values remain visible where available.`); else { setProfileLoadError(null); setProfileRetrievedAt(new Date()); }
    } catch (error) {
      console.error('Error loading user data:', error);
      if (revision === profileLoadRevisionRef.current && loadedProfileUserIdRef.current === ownerId) {
        setProfileLoadError('We could not refresh your account workspace. Retry to reload schedules, plans, lists, and friend counts.');
      }
    } finally {
      if (revision === profileLoadRevisionRef.current) setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!loading && !user) {
      loadedProfileUserIdRef.current = null;
      academicOwnerRef.current = null;
      completedCourseBaselineRef.current = null;
      gradeBaselineRef.current = null;
      profileLoadRevisionRef.current += 1;
      academicLoadRevisionRef.current += 1;
      academicSaveRevisionRef.current += 1;
      academicSavePendingRef.current = false;
      router.replace(`/login?redirect=${encodeURIComponent(routeRedirectTarget)}`);
    }
    else if (user && loadedProfileUserIdRef.current !== user.id) {
      loadedProfileUserIdRef.current = user.id;
      profileLoadRevisionRef.current += 1;
      academicLoadRevisionRef.current += 1;
      academicSaveRevisionRef.current += 1;
      academicSavePendingRef.current = false;
      academicOwnerRef.current = null;
      completedCourseBaselineRef.current = null;
      gradeBaselineRef.current = null;
      setCompletedCourses([]);
      setCourseGrades([]);
      setLastSaved(null);
      setAcademicSaveError(null);
      void loadUserData();
      void loadMemories();
    }
  }, [loadMemories, loadUserData, loading, routeRedirectTarget, router, user]);
  useEffect(() => { const requested = searchParams.get('tab'); const nextTab = forcedTab ?? (isProfileTabId(requested) ? requested : 'profile'); if (nextTab !== activeTab) setActiveTab(nextTab); }, [activeTab, forcedTab, searchParams]);
  useEffect(() => {
    if (pendingTabFocusRef.current !== activeTab) return;
    const index = profileTabIds.indexOf(activeTab);
    const frame = window.requestAnimationFrame(() => { tabRefs.current[index]?.focus(); pendingTabFocusRef.current = null; });
    return () => window.cancelAnimationFrame(frame);
  }, [activeTab]);

  const loadCourses = useCallback(async () => { try { setAllCourses(await loadCatalogCourses()); } catch (error) { console.error('Error loading courses:', error); } }, []);
  useEffect(() => { if (!user || allCourses.length || (activeTab !== 'previous_classes' && activeTab !== 'courses')) return; void loadCourses(); }, [activeTab, allCourses.length, loadCourses, user]);
  useEffect(() => { if (activeTab === 'billing') void loadBilling(); }, [activeTab, loadBilling]);

  const openConfirm = useCallback((title: string, description: string, action: () => Promise<void>) => {
    setConfirmTitle(title); setConfirmMessage(description);
    confirmActionRef.current = async () => {
      setConfirmBusy(true);
      try {
        await action();
        setConfirmOpen(false);
      } catch (error) {
        setConfirmOpen(false);
        setMessage(error instanceof Error ? error.message : 'The requested profile change could not be completed.');
        setMessageOpen(true);
      } finally {
        setConfirmBusy(false);
      }
    };
    setConfirmOpen(true);
  }, []);
  const showMessage = useCallback((nextMessage: string) => { setMessage(nextMessage); setMessageOpen(true); }, []);

  const handleTabKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % profileTabIds.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + profileTabIds.length) % profileTabIds.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = profileTabIds.length - 1;
    if (nextIndex === null) return;
    event.preventDefault(); const tab = profileTabIds[nextIndex]; if (tab === 'memories') void loadMemories(); pendingTabFocusRef.current = tab; handleTabChange(tab);
  }, [handleTabChange, loadMemories]);
  const handlePanelTabChange = useCallback((tab: ProfileTabId) => { if (tab === 'memories') void loadMemories(); handleTabChange(tab); }, [handleTabChange, loadMemories]);

  const reloadAcademicRecord = useCallback(async () => {
    if (!user?.id) return;
    if (academicSavePendingRef.current) return;
    const ownerId = user.id;
    const revision = ++academicLoadRevisionRef.current;
    try {
      const { loadUserAcademicData } = await import('@/app/services/userAcademicData');
      const record = await loadUserAcademicData(ownerId, { fresh: true });
      if (revision !== academicLoadRevisionRef.current || loadedProfileUserIdRef.current !== ownerId) return;
      if (!record) {
        setAcademicSaveError('The saved academic record could not be refreshed. Your edits remain on this screen.');
        return;
      }
      setCompletedCourses(record.completedCourses);
      setCourseGrades(record.courseGrades || []);
      setLastSaved(record.lastUpdated);
      completedCourseBaselineRef.current = record.completedCourses.map((course) => course.baseId);
      gradeBaselineRef.current = record.courseGrades || [];
      academicOwnerRef.current = ownerId;
      setAcademicSaveError(null);
    } catch {
      if (revision !== academicLoadRevisionRef.current || loadedProfileUserIdRef.current !== ownerId) return;
      setAcademicSaveError('The saved academic record could not be refreshed. Your edits remain on this screen.');
    }
  }, [user?.id]);

  const saveCompletedCoursesToDB = useCallback(async (courses: RutgersCourse[]) => {
    const loadedBaseIds = completedCourseBaselineRef.current;
    if (!user?.id || academicOwnerRef.current !== user.id || !loadedBaseIds || academicSaveError || academicSavePendingRef.current) {
      setAcademicSaveError('Your course changes are not saved. Reload the academic record before saving.');
      return false;
    }
    academicSavePendingRef.current = true;
    const saveRevision = ++academicSaveRevisionRef.current;
    academicLoadRevisionRef.current += 1;
    setIsSavingCourses(true);
    try {
      const { saveCompletedCourses } = await import('@/app/services/userAcademicData');
      const saved = await saveCompletedCourses(user.id, courses, loadedBaseIds);
      if (saveRevision !== academicSaveRevisionRef.current || loadedProfileUserIdRef.current !== user.id) return false;
      if (saved) {
        academicLoadRevisionRef.current += 1;
        completedCourseBaselineRef.current = courses.map((course) => course.baseId);
        setLastSaved(new Date().toISOString());
      } else setAcademicSaveError('Your course changes are not saved. The academic record may have changed. Reload it to review the saved version.');
      return saved;
    } catch {
      if (saveRevision === academicSaveRevisionRef.current) setAcademicSaveError('Your course changes are not saved. Reload the academic record before saving again.');
      return false;
    }
    finally { if (saveRevision === academicSaveRevisionRef.current) { academicSavePendingRef.current = false; setIsSavingCourses(false); } }
  }, [academicSaveError, user?.id]);
  const handleAddCourse = useCallback(async (course: RutgersCourse) => { if (completedCourses.some((item) => item.baseId === course.baseId)) return setShowAddCourse(false); const next = [...completedCourses, course]; setCompletedCourses(next); await saveCompletedCoursesToDB(next); setShowAddCourse(false); }, [completedCourses, saveCompletedCoursesToDB]);
  const handleRemoveCourse = useCallback((course: RutgersCourse) => { openConfirm('Remove completed course', `Remove ${course.courseName} from your completed coursework?`, async () => { const next = completedCourses.filter((item) => item.id !== course.id); setCompletedCourses(next); await saveCompletedCoursesToDB(next); }); }, [completedCourses, openConfirm, saveCompletedCoursesToDB]);
  const handleGradeChange = useCallback(async (course: RutgersCourse, grade: string) => {
    if (!user?.id) return;
    const byCode = new Map(courseGrades.map((entry) => [entry.code.replace(/[^0-9]/g, ''), entry]));
    byCode.set(course.baseId, { ...byCode.get(course.baseId), code: course.baseId, grade });
    const next = Array.from(byCode.values());
    setCourseGrades(next);
    const loadedGrades = gradeBaselineRef.current;
    if (academicOwnerRef.current !== user.id || !loadedGrades || academicSaveError || academicSavePendingRef.current) {
      setAcademicSaveError('Your grade changes are not saved. Reload the academic record before saving.');
      return;
    }
    academicSavePendingRef.current = true;
    const saveRevision = ++academicSaveRevisionRef.current;
    academicLoadRevisionRef.current += 1;
    setIsSavingCourses(true);
    try {
      const { saveCourseGrades } = await import('@/app/services/userAcademicData');
      const saved = await saveCourseGrades(user.id, next, loadedGrades);
      if (saveRevision !== academicSaveRevisionRef.current || loadedProfileUserIdRef.current !== user.id) return;
      if (saved) {
        academicLoadRevisionRef.current += 1;
        gradeBaselineRef.current = next;
        setLastSaved(new Date().toISOString());
      } else setAcademicSaveError('Your grade changes are not saved. The academic record may have changed. Reload it to review the saved version.');
    } catch {
      if (saveRevision === academicSaveRevisionRef.current) setAcademicSaveError('Your grade changes are not saved. Reload the academic record before saving again.');
    } finally {
      if (saveRevision === academicSaveRevisionRef.current) {
        academicSavePendingRef.current = false;
        setIsSavingCourses(false);
      }
    }
  }, [academicSaveError, courseGrades, user?.id]);

  const handleSaveAcademic = useCallback(async () => {
    const errors = validateProfileAcademicData(academicData);
    setAcademicErrors(errors); if (Object.keys(errors).length) return;
    try { await markOnboardingComplete(academicData); setIsEditingAcademic(false); setAcademicErrors({}); }
    catch (error) { console.error('Error updating onboarding data:', error); showMessage('Academic information could not be saved. Please try again.'); }
  }, [academicData, markOnboardingComplete, showMessage]);

  const resetCustomPaletteBuilder = useCallback(() => { setCustomPaletteName(''); setCustomPaletteDescription(''); setCustomPaletteError(null); setCustomCourseSwatches(defaultCourseSwatches); setCustomProgramSwatches(defaultProgramSwatches); }, []);
  const updateCustomSwatch = useCallback((kind: 'course' | 'program', index: number, field: 'label' | 'solid', value: string) => { const setter = kind === 'course' ? setCustomCourseSwatches : setCustomProgramSwatches; setter((current) => current.map((swatch, swatchIndex) => swatchIndex === index ? { ...swatch, [field]: value } : swatch)); }, []);
  const saveCustomPalette = useCallback(() => {
    const name = customPaletteName.trim();
    if (!name) return setCustomPaletteError('Add a palette name before saving.');
    if (academicPalettePresets.some((preset) => preset.name.trim().toLowerCase() === name.toLowerCase())) return setCustomPaletteError('Choose a different palette name. That one already exists.');
    const preset = createCustomAcademicPalettePreset({ name, description: customPaletteDescription, courseSwatches: customCourseSwatches, programSwatches: customProgramSwatches });
    addCustomAcademicPalette(preset); setCoursePaletteId(preset.id); setProgramPaletteId(preset.id); setShowCustomPaletteBuilder(false); resetCustomPaletteBuilder();
  }, [academicPalettePresets, addCustomAcademicPalette, customCourseSwatches, customPaletteDescription, customPaletteName, customProgramSwatches, resetCustomPaletteBuilder, setCoursePaletteId, setProgramPaletteId]);

  const filteredCourses = useMemo(() => filterUniqueProfileCourses(allCourses, courseSearchQuery), [allCourses, courseSearchQuery]);
  const friendsCount = connectionsSummary?.acceptedCount ?? null;

  const profileOverviewCards = useMemo(() => [
    { label: 'Saved schedules', value: String(savedSchedules.length), detail: primaryScheduleId ? 'A primary schedule is set for planning defaults.' : 'Choose a primary schedule for planning defaults.' },
    { label: 'Friends', value: friendsCount == null ? 'Unavailable' : String(friendsCount), detail: friendsCount == null ? 'The friendship total could not be retrieved.' : 'Accepted friends can see Public schedules.' },
    { label: 'Degree plans', value: degreePlansRetrieved ? String(degreePlans.length) : 'Unavailable', detail: degreePlansRetrieved ? 'Roadmaps available to planning tools.' : 'The degree-plan service is unavailable.' },
  ], [degreePlans.length, degreePlansRetrieved, friendsCount, primaryScheduleId, savedSchedules.length]);
  const profileViewModel = useMemo(() => createProfileViewModel({
    state: profileLoadError ? (userObject || profileRetrievedAt ? 'partial' : 'error') : 'populated', activeTab,
    displayName: userObject?.display_name || 'Your Scarlet Sync account', email: userObject?.email || null, avatarUrl: userObject?.profile_picture_url || null,
    sourceUpdatedAt: profileRetrievedAt ? profileRetrievedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null,
    sourceStale: Boolean(profileLoadError), error: profileLoadError, completedCourseCount: completedCourses.length, memoryCount: memories.length,
    metricInput: { savedScheduleCount: savedSchedules.length, courseListCount: courseLists.length, degreePlanCount: degreePlansRetrieved ? degreePlans.length : null, friendsCount },
  }), [activeTab, completedCourses.length, courseLists.length, degreePlans.length, degreePlansRetrieved, friendsCount, memories.length, profileLoadError, profileRetrievedAt, savedSchedules.length, userObject]);

  if (loading || isLoading) return <PageLoading title="Loading your Scarlet Sync profile" description="Retrieving account, academic, planning, and community data." />;
  const displayName = userObject?.display_name || user?.email?.split('@')[0] || 'Scarlet student';
  const email = userObject?.email || user?.email || '';

  return (
    <>
      <ProfileWorkspaceView
        embedded={embedded} summary={profileViewModel} activeTab={activeTab} displayName={displayName} email={email} avatarUrl={userObject?.profile_picture_url || null}
        subscriptionLabel={userObject?.subscription_tier === 'pro' ? 'Pro plan' : 'Free plan'} completedCourseCount={completedCourses.length} memoryCount={memories.length}
        onRetry={() => void loadUserData()} onSignOut={() => { void signOut(); router.push('/login'); }} onTabChange={handlePanelTabChange} onTabKeyDown={handleTabKeyDown} tabRefs={tabRefs}
        account={{
          displayName, email, overviewCards: profileOverviewCards, academicData, academicErrors, isEditingAcademic, academicSaving: onboardingLoading, degrees: allDegrees, majorSearch,
          onMajorSearchChange: setMajorSearch, onAcademicDataChange: setAcademicData, onToggleAcademicEdit: () => { setIsEditingAcademic((value) => !value); setAcademicErrors({}); },
          onCancelAcademicEdit: () => { setIsEditingAcademic(false); setAcademicErrors({}); }, onSaveAcademic: handleSaveAcademic,
          feedbackMode: interactionFeedbackMode, onFeedbackModeChange: setInteractionFeedbackPreference,
          palettePreferences: academicPalettePreferences, palettePresets: academicPalettePresets, explorerPreferences,
          onExplorerDefaultViewChange: setExplorerDefaultView, onExplorerDepartmentColorsChange: setExplorerDepartmentColorsEnabled,
          onCoursePaletteChange: setCoursePaletteId, onProgramPaletteChange: setProgramPaletteId, onDeleteCustomPalette: deleteCustomAcademicPalette, onResetPalettes: resetAcademicPalettePreferences,
          showCustomPaletteBuilder, onToggleCustomPaletteBuilder: () => setShowCustomPaletteBuilder((value) => { if (value) resetCustomPaletteBuilder(); return !value; }),
          customPaletteName, customPaletteDescription, customPaletteError, customCourseSwatches, customProgramSwatches,
          onCustomPaletteNameChange: setCustomPaletteName, onCustomPaletteDescriptionChange: setCustomPaletteDescription, onCustomSwatchChange: updateCustomSwatch,
          onSaveCustomPalette: saveCustomPalette, onResetCustomPaletteBuilder: resetCustomPaletteBuilder,
          onDeleteAccount: () => openConfirm('Delete account', 'Permanently delete your account and all schedules, course lists, degree plans, and profile information?', async () => {
            if (!user?.id) return;
            try { await usersApi.deleteAccount(user.id); showMessage('Account deleted successfully. You will be redirected to login.'); window.setTimeout(() => { void signOut(); router.push('/login'); }, 2000); }
            catch (error) { showMessage(`Failed to delete account: ${error instanceof Error ? error.message : 'Unknown error'}`); }
          }),
        }}
        previousClasses={{
          completedCourses, grades: courseGrades, lastSaved, saving: isSavingCourses, saveError: academicSaveError, onReloadRecord: () => void reloadAcademicRecord(), addDialogOpen: showAddCourse, searchQuery: courseSearchQuery, searchResults: filteredCourses,
          onSearchQueryChange: setCourseSearchQuery, onOpenAddDialog: () => setShowAddCourse(true), onCloseAddDialog: () => setShowAddCourse(false), onAddCourse: handleAddCourse,
          onRemoveCourse: handleRemoveCourse, onGradeChange: handleGradeChange, onOpenPrerequisiteChecker: () => router.push('/prerequisite-checker'),
        }}
        connections={{
          acceptedCount: connectionsSummary?.acceptedCount ?? null,
          incomingRequestCount: connectionsSummary?.incomingRequestCount ?? null,
          sentRequestCount: connectionsSummary?.sentRequestCount ?? null,
          publicScheduleCount: savedSchedules.filter((schedule) => schedule.is_public).length,
        }}
        schedules={{
          schedules: savedSchedules, primaryScheduleId, onOpenScheduling: () => router.push('/scheduling'), onOpenSchedule: (id) => router.push(`/scheduling?id=${encodeURIComponent(id)}`),
          onDeleteSchedule: (schedule) => openConfirm('Delete schedule', 'Delete this saved schedule? This cannot be undone.', async () => { const id = String(schedule.schedule_id); await schedulesApi.delete(id); setSavedSchedules((current) => current.filter((item) => String(item.schedule_id) !== id)); }),
        }}
        courseLists={{
          lists: courseLists, onOpenCourseLists: () => router.push('/course-lists'), onOpenList: (id) => router.push(`/course-list?id=${encodeURIComponent(id)}`),
          onShareList: (list) => { const id = String(list.id); void navigator.clipboard.writeText(`${window.location.origin}/course-list?id=${encodeURIComponent(id)}`).then(() => showMessage('Link copied to clipboard.')); },
          onDeleteList: (list) => openConfirm('Delete course list', 'Delete this course list? This cannot be undone.', async () => { const id = String(list.id); await courseListsApi.delete(Number(id)); setCourseLists((current) => current.filter((item) => String(item.id) !== id)); }),
        }}
        degreePlans={{
          plans: degreePlans, retrieved: degreePlansRetrieved, onRetry: () => void loadUserData(), onOpenPlanner: () => router.push('/degree-planning'), onOpenPlan: (id) => router.push(`/degree-planning?planId=${encodeURIComponent(id)}`),
          onDeletePlan: (plan) => openConfirm('Delete degree plan', 'Delete this degree plan? This cannot be undone.', async () => { await degreePlansApi.delete(Number(plan.id)); setDegreePlans((current) => current.filter((item) => item.id !== plan.id)); }),
        }}
        billing={{
          ...billing,
          checkoutState: searchParams.get('checkout') === 'processing' ? 'processing' : searchParams.get('checkout') === 'cancelled' ? 'cancelled' : null,
          actionBusy: billingActionBusy,
          onStartCheckout: () => void openBillingCheckout(),
          onOpenPortal: () => void openBillingPortal(),
          onRetry: () => void loadBilling(),
        }}
        professionalContent={<ProfessionalInfoSection userId={user?.id} />}
        memories={{
          memories, loading: isLoadingMemories, onOpenAssistant: () => router.push('/ai-assistant'),
          onDeleteMemory: (memory) => openConfirm('Delete memory', `Delete “${memory.memory_key}”? This cannot be undone.`, async () => { if (!user?.id) return; const { error } = await supabase.from('ai_user_memories').delete().eq('id', memory.id).eq('user_id', user.id); if (error) throw error; setMemories((current) => current.filter((item) => item.id !== memory.id)); }),
        }}
      />
      <Modal isOpen={messageOpen} onClose={() => setMessageOpen(false)} title="Profile update" size="sm"><p className="text-sm text-[var(--ss-text-muted)]">{message}</p><div className="mt-4 flex justify-end"><Button onClick={() => setMessageOpen(false)}>OK</Button></div></Modal>
      <Modal isOpen={confirmOpen} onClose={() => { if (!confirmBusy) setConfirmOpen(false); }} title={confirmTitle} size="sm"><p className="text-sm text-[var(--ss-text-muted)]">{confirmMessage}</p><div className="mt-4 flex justify-end gap-2"><Button intent="secondary" disabled={confirmBusy} onClick={() => setConfirmOpen(false)}>Cancel</Button><Button intent="danger" loading={confirmBusy} loadingLabel="Deleting…" onClick={() => void confirmActionRef.current?.()}>Delete</Button></div></Modal>
    </>
  );
}

// Query-dependent interactive content owns its loading boundary.
export default function RoutePage() { return <RouteSuspense fallback={<RouteLoading title="Preparing your workspace" />}><ProfilePage /></RouteSuspense>; }
